import { useState, useEffect, useMemo, useRef } from "react";
import {
  VStack,
  Heading,
  Button,
  Textarea,
  Input,
  Box,
  Text,
  useToast,
  HStack,
  Badge,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { AddIcon, InfoIcon } from "@chakra-ui/icons";
import styled from "styled-components";
import axios from "axios";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import {
  Campaign,
  createScheduledMessage,
  updateScheduledMessage,
  deleteScheduledMessage,
} from "../../api/admin/adminCampaign";
import { DragDrop, LogoFile } from "../DragDrop";
import { rootUrl } from "../../api/rootUrl";
import { format } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { SendTestMessageModal } from "./modals/SendTestMessageModal";
import { SegmentedMessage } from "sms-segments-calculator";
import { ImageAssetPicker } from "./ImageAssetPicker";
import { AttachmentIcon } from "@chakra-ui/icons";

const MessageCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const CharCount = styled.span<{ $isOver: boolean; $isUnicode?: boolean }>`
  font-size: 0.875rem;
  color: ${(props) =>
    props.$isOver ? "#e53e3e" : props.$isUnicode ? "#dd6b20" : "#2f855a"};
  font-weight: ${(props) => (props.$isOver ? "600" : "500")};
`;

const ImagePreview = styled.img`
  width: 200px;
  height: auto;
  max-height: 150px;
  object-fit: cover;
  border-radius: 8px;
  margin-top: 0.5rem;
`;

interface SuggestedMessage {
  text: string;
  scheduledFor?: string;
}

interface Props {
  campaign: Campaign;
  aiSuggestedMessages?: SuggestedMessage[];
  recipientCount?: number | null;
}

interface MessageInput {
  id: string;
  text: string;
  scheduledFor: string;
  socialShareImage?: string;
}

export const MessageBuilder = ({
  campaign,
  aiSuggestedMessages,
  recipientCount,
}: Props) => {
  const { adminToken } = useAdminAuth();
  const toast = useToast();
  const [message, setMessage] = useState<MessageInput>({
    id: "1",
    text: "",
    scheduledFor: "",
    socialShareImage: campaign.socialShareImage || "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(
    null
  );
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isPickerOpen,
    onOpen: onPickerOpen,
    onClose: onPickerClose,
  } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const [selectedMessageText, setSelectedMessageText] = useState("");
  const [selectedSocialShareImage, setSelectedSocialShareImage] = useState<
    string | undefined
  >();
  const [messageToDelete, setMessageToDelete] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmCancelRef = useRef<HTMLButtonElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const sectionTopRef = useRef<HTMLDivElement>(null);

  // Calculate SMS segments in real-time
  const segmentInfo = useMemo(() => {
    const optOutText = " STOP to opt out.";
    // Use same format as actual short URLs: https://api.skipgive.com/s/e729940d (37 chars)
    const placeholderUrl = "https://api.skipgive.com/s/abcd1234";
    const fullMessage = `${message.text} ${optOutText}\n${placeholderUrl}`;

    const segmented = new SegmentedMessage(fullMessage);
    const isGSM = segmented.encodingName === "GSM-7";
    const maxTotal = isGSM ? 160 : 70;
    const overhead = optOutText.length + placeholderUrl.length + 1; // +1 for newline
    const maxUserChars = maxTotal - overhead;
    const nonGSMChars = segmented.getNonGsmCharacters();

    return {
      segments: segmented.segmentsCount,
      encoding: segmented.encodingName,
      maxUserChars,
      isValid: segmented.segmentsCount === 1,
      nonGSMChars,
      currentChars: message.text.length,
      charsOver: Math.max(0, message.text.length - maxUserChars),
    };
  }, [message.text]);

  useEffect(() => {
    // Initialize with first AI suggested message if available
    if (aiSuggestedMessages && aiSuggestedMessages.length > 0) {
      const firstMsg = aiSuggestedMessages[0];
      setMessage({
        id: "ai-0",
        text: firstMsg.text,
        scheduledFor: firstMsg.scheduledFor || "",
        socialShareImage: campaign.socialShareImage || "",
      });
      setShowForm(true);
      toast({
        title: "AI-suggested message loaded! 📱",
        description: "Review and schedule one message at a time",
        status: "info",
        duration: 3000,
      });
    } else if (
      !campaign.scheduledMessages ||
      campaign.scheduledMessages.length === 0
    ) {
      // Show form by default if no scheduled messages exist
      setShowForm(true);
    }
  }, [campaign, aiSuggestedMessages, toast]);

  const updateMessage = (field: keyof MessageInput, value: string) => {
    // Auto-replace Unicode characters with GSM equivalents
    if (field === "text") {
      value = value
        .replace(/[\u2018\u2019]/g, "'") // Replace curly single quotes with straight apostrophe
        .replace(/[\u201C\u201D]/g, '"') // Replace curly double quotes with straight quotes
        .replace(/[\u2013\u2014]/g, "-") // Replace en-dash and em-dash with hyphen
        .replace(/\u2026/g, "..."); // Replace ellipsis with three periods
    }
    setMessage({ ...message, [field]: value });
  };

  const clearMessage = () => {
    setMessage({
      id: Date.now().toString(),
      text: "",
      scheduledFor: "",
      socialShareImage: campaign.socialShareImage || "",
    });
  };

  const handleSendTest = (messageText: string, socialShareImage?: string) => {
    setSelectedMessageText(messageText);
    setSelectedSocialShareImage(socialShareImage);
    onOpen();
  };

  const handleImageUpload = async (file: LogoFile) => {
    if (!adminToken) return;
    try {
      const result = await axios.post(
        `${rootUrl}/admin/upload`,
        {
          file: file.base64,
          type: "SOCIAL_SHARE_IMAGE",
          mimeType: file.type,
          name: file.name,
          clientId: campaign.clientId,
        },
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        }
      );

      const imageUrl = result.data.url;
      updateMessage("socialShareImage", imageUrl);

      toast({
        title: "Image uploaded successfully",
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Failed to upload image",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleSelectFromLibrary = (url: string) => {
    updateMessage("socialShareImage", url);
  };

  const handleNewMessage = () => {
    clearMessage();
    setEditingMessageId(null);
    setShowForm(true);
    // Scroll to top of message section
    setTimeout(() => {
      sectionTopRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
  };

  const handleEditMessage = (msg: any) => {
    // Convert UTC time back to local timezone for the input
    const localDate = new Date(msg.scheduledFor);
    const localDateString = format(localDate, "yyyy-MM-dd'T'HH:mm");

    setMessage({
      id: msg.id,
      text: msg.messageText,
      scheduledFor: localDateString,
      socialShareImage: msg.socialShareImage || campaign.socialShareImage || "",
    });
    setEditingMessageId(msg.id);
    setShowForm(true);

    // Scroll to top of message section
    setTimeout(() => {
      sectionTopRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
  };

  const handleCancelEdit = () => {
    clearMessage();
    setEditingMessageId(null);
    // Hide form if there are existing messages
    if (campaign.scheduledMessages && campaign.scheduledMessages.length > 0) {
      setShowForm(false);
    }
  };

  const handleDeleteMessage = (messageId: string) => {
    setMessageToDelete(messageId);
    onDeleteOpen();
  };

  const confirmDeleteMessage = async () => {
    if (!adminToken || !messageToDelete) return;

    setDeletingMessageId(messageToDelete);
    onDeleteClose();

    try {
      await deleteScheduledMessage(adminToken, campaign.id, messageToDelete);
      toast({
        title: "Message deleted successfully",
        status: "success",
        duration: 2000,
      });

      // Reload to show updated list
      window.location.reload();
    } catch (error: any) {
      toast({
        title: "Failed to delete message",
        description: error?.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000,
      });
    } finally {
      setDeletingMessageId(null);
      setMessageToDelete(null);
    }
  };

  const handleSchedule = () => {
    if (!message.text || !message.scheduledFor) {
      toast({
        title: "Please fill in both message and schedule time",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!segmentInfo.isValid) {
      toast({
        title: `Message too long (${segmentInfo.segments} segments)`,
        description: `Remove ${segmentInfo.charsOver} character${
          segmentInfo.charsOver !== 1 ? "s" : ""
        } to fit in 1 SMS segment`,
        status: "error",
        duration: 5000,
      });
      return;
    }

    // Show confirmation modal
    onConfirmOpen();
  };

  const confirmSchedule = async () => {
    if (!adminToken) return;

    setIsSaving(true);
    onConfirmClose();

    try {
      const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const scheduledMessage = {
        messageText: message.text,
        scheduledFor: fromZonedTime(
          new Date(message.scheduledFor),
          userTz
        ).toISOString(),
        socialShareImage: message.socialShareImage || undefined,
      };

      if (editingMessageId) {
        // Use proper update endpoint instead of delete+create
        await updateScheduledMessage(
          adminToken,
          campaign.id,
          editingMessageId,
          scheduledMessage
        );
      } else {
        await createScheduledMessage(adminToken, campaign.id, scheduledMessage);
      }

      toast({
        title: editingMessageId
          ? "Message updated successfully! 🎉"
          : "Message scheduled successfully! 🎉",
        status: "success",
        duration: 3000,
      });

      // Clear the form and editing state
      clearMessage();
      setEditingMessageId(null);

      // Refresh to show the updated message list
      window.location.reload();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to schedule message";
      toast({
        title: editingMessageId
          ? "Failed to update message"
          : "Failed to schedule message",
        description: errorMessage,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Scroll target - top of messages section */}
      <Box ref={sectionTopRef} />

      {/* Header with New Message button */}
      {campaign.scheduledMessages && campaign.scheduledMessages.length > 0 && (
        <HStack justify="space-between" align="center">
          <Heading size="sm">Scheduled Messages</Heading>
          {!showForm && (
            <Button
              leftIcon={<AddIcon />}
              size="sm"
              colorScheme="blue"
              onClick={handleNewMessage}
            >
              New Message
            </Button>
          )}
        </HStack>
      )}

      {/* Create/Edit Form - shows above the list */}
      {showForm && (
        <>
          <Box ref={formRef}>
            {editingMessageId && (
              <Box p={2} bg="blue.50" borderRadius="md" mb={2}>
                <Text fontSize="sm" color="blue.700" fontWeight="600">
                  ✏️ Editing message
                </Text>
              </Box>
            )}
          </Box>

          <MessageCard>
            <VStack align="stretch" spacing={3}>
              <Box>
                <Textarea
                  value={message.text}
                  onChange={(e) => updateMessage("text", e.target.value)}
                  placeholder="Join us tonight! Every gift makes a difference for families in need..."
                  rows={3}
                />
                <VStack align="stretch" spacing={1} mt={2}>
                  <HStack justify="space-between">
                    <CharCount
                      $isOver={!segmentInfo.isValid}
                      $isUnicode={segmentInfo.encoding === "UCS-2"}
                    >
                      {segmentInfo.currentChars}/{segmentInfo.maxUserChars}{" "}
                      chars
                      {segmentInfo.encoding === "UCS-2" && " (Unicode)"}
                    </CharCount>
                    {segmentInfo.isValid ? (
                      <Badge colorScheme="green" fontSize="0.75rem">
                        1 SEGMENT ✓
                      </Badge>
                    ) : (
                      <Badge colorScheme="red" fontSize="0.75rem">
                        {segmentInfo.segments} SEGMENTS
                      </Badge>
                    )}
                  </HStack>
                  {segmentInfo.encoding === "UCS-2" &&
                    segmentInfo.nonGSMChars.length > 0 && (
                      <Text fontSize="xs" color="orange.600" fontWeight="500">
                        Unicode chars detected:{" "}
                        {segmentInfo.nonGSMChars
                          .map((c) => `"${c}"`)
                          .join(", ")}
                      </Text>
                    )}
                </VStack>
              </Box>

              <Input
                type="datetime-local"
                value={message.scheduledFor}
                onChange={(e) => updateMessage("scheduledFor", e.target.value)}
                placeholder="Schedule time (local)"
              />

              <FormControl>
                <FormLabel fontSize="sm">
                  Social Share Image (Optional)
                </FormLabel>
                <Button
                  onClick={onPickerOpen}
                  size="sm"
                  variant="outline"
                  leftIcon={<AttachmentIcon />}
                  mb={2}
                >
                  Select from Library
                </Button>
                <DragDrop
                  onLogoChange={handleImageUpload}
                  label="Upload or drop image"
                />
                {message.socialShareImage && (
                  <ImagePreview
                    src={message.socialShareImage}
                    alt="Social share"
                  />
                )}
              </FormControl>

              {/* Info note about test messages */}
              <HStack spacing={2} p={2} bg="gray.50" borderRadius="md">
                <InfoIcon color="gray.500" boxSize={3} />
                <Text fontSize="xs" color="gray.600">
                  Test messages use default campaign image and shortened URL
                </Text>
              </HStack>
            </VStack>
          </MessageCard>

          <HStack spacing={2}>
            {message.text && (
              <Button
                onClick={() =>
                  handleSendTest(message.text, message.socialShareImage)
                }
                size="sm"
                variant="outline"
                colorScheme="blue"
              >
                Send Test
              </Button>
            )}
            <Button
              onClick={handleCancelEdit}
              size="sm"
              variant="outline"
              colorScheme="gray"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSchedule}
              colorScheme="green"
              size="md"
              isLoading={isSaving}
              isDisabled={
                !segmentInfo.isValid || !message.text || !message.scheduledFor
              }
              flex={1}
            >
              {editingMessageId ? "Update" : "Schedule"}
            </Button>
          </HStack>
        </>
      )}

      {/* Existing Scheduled Messages List */}
      {campaign.scheduledMessages && campaign.scheduledMessages.length > 0 && (
        <>
          {/* Pending Messages */}
          {campaign.scheduledMessages.filter((msg) => msg.status === "PENDING")
            .length > 0 && (
            <VStack spacing={2} align="stretch">
              {campaign.scheduledMessages
                .filter((msg) => msg.status === "PENDING")
                .sort((a, b) => new Date(a.scheduledFor).getTime() - new Date(b.scheduledFor).getTime())
                .map((msg) => (
                  <MessageCard key={msg.id}>
                    <VStack align="stretch" spacing={2}>
                      <HStack justify="space-between" align="start">
                        <Box flex={1}>
                          <HStack mb={2}>
                            <Badge colorScheme="blue">{msg.status}</Badge>
                            <Text fontSize="xs" color="gray.500">
                              Scheduled:{" "}
                              {format(
                                new Date(msg.scheduledFor),
                                "MMM d, yyyy 'at' h:mm a"
                              )}
                            </Text>
                          </HStack>
                          <Text fontSize="sm">{msg.messageText}</Text>
                          <Text fontSize="xs" color="blue.500" mt={1}>
                            [shortened-url]
                          </Text>
                          {msg.socialShareImage && (
                            <Box mt={2}>
                              <Text fontSize="xs" color="gray.600" mb={1}>
                                Social Share Image:
                              </Text>
                              <img
                                src={msg.socialShareImage}
                                alt="Social share"
                                style={{
                                  width: "100%",
                                  maxWidth: "120px",
                                  borderRadius: "4px",
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                        <HStack>
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() =>
                              handleSendTest(
                                msg.messageText,
                                msg.socialShareImage || undefined
                              )
                            }
                          >
                            Test
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="gray"
                            variant="outline"
                            onClick={() => handleEditMessage(msg)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleDeleteMessage(msg.id)}
                            isLoading={deletingMessageId === msg.id}
                          >
                            Delete
                          </Button>
                        </HStack>
                      </HStack>
                    </VStack>
                  </MessageCard>
                ))}
            </VStack>
          )}

          {/* Completed Messages - Divider and Section */}
          {campaign.scheduledMessages.filter(
            (msg) => msg.status === "SENT" || msg.status === "FAILED"
          ).length > 0 && (
            <>
              <Box my={4} borderTop="2px solid" borderColor="gray.300" />
              <Heading size="xs" color="gray.500">
                Completed
              </Heading>
              <VStack spacing={2} align="stretch">
                {campaign.scheduledMessages
                  .filter(
                    (msg) => msg.status === "SENT" || msg.status === "FAILED"
                  )
                  .sort((a, b) => new Date(b.scheduledFor).getTime() - new Date(a.scheduledFor).getTime())
                  .map((msg) => (
                    <MessageCard key={msg.id}>
                      <VStack align="stretch" spacing={2}>
                        <HStack justify="space-between" align="start">
                          <Box flex={1}>
                            <HStack mb={2}>
                              <Badge
                                colorScheme={
                                  msg.status === "SENT" ? "green" : "red"
                                }
                              >
                                {msg.status}
                              </Badge>
                              <Text fontSize="xs" color="gray.500">
                                Scheduled:{" "}
                                {format(
                                  new Date(msg.scheduledFor),
                                  "MMM d, yyyy 'at' h:mm a"
                                )}
                              </Text>
                            </HStack>
                            <Text fontSize="sm">{msg.messageText}</Text>
                            <Text fontSize="xs" color="blue.500" mt={1}>
                              [shortened-url]
                            </Text>
                            {msg.socialShareImage && (
                              <Box mt={2}>
                                <Text fontSize="xs" color="gray.600" mb={1}>
                                  Social Share Image:
                                </Text>
                                <img
                                  src={msg.socialShareImage}
                                  alt="Social share"
                                  style={{
                                    width: "100%",
                                    maxWidth: "120px",
                                    borderRadius: "4px",
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </HStack>
                      </VStack>
                    </MessageCard>
                  ))}
              </VStack>
            </>
          )}
        </>
      )}

      <SendTestMessageModal
        isOpen={isOpen}
        onClose={onClose}
        campaignId={campaign.id}
        messageText={selectedMessageText}
        socialShareImage={selectedSocialShareImage}
      />

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Message
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this scheduled message? This
              action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDeleteMessage} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <ImageAssetPicker
        isOpen={isPickerOpen}
        onClose={onPickerClose}
        clientId={campaign.clientId}
        onSelectImage={handleSelectFromLibrary}
      />

      {/* Schedule Confirmation Dialog */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={confirmCancelRef}
        onClose={onConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {editingMessageId
                ? "Update Scheduled Message"
                : "Schedule New Message"}
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="stretch" spacing={3}>
                <Text>
                  {editingMessageId
                    ? "Are you sure you want to update this scheduled message?"
                    : `This message will be sent to ${
                        recipientCount || 0
                      } recipient${recipientCount !== 1 ? "s" : ""}.`}
                </Text>
                <Box
                  p={3}
                  bg="blue.50"
                  borderRadius="md"
                  borderLeft="3px solid"
                  borderLeftColor="blue.400"
                >
                  <Text fontSize="sm" fontWeight="600" mb={2}>
                    Message Preview:
                  </Text>
                  <Text fontSize="sm" mb={2}>
                    {message.text}
                  </Text>
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    STOP to opt out
                  </Text>
                  <Text fontSize="xs" color="blue.500">
                    [shortened-url]
                  </Text>
                </Box>
                <Box p={3} bg="gray.50" borderRadius="md">
                  <HStack justify="space-between" fontSize="sm">
                    <Text fontWeight="600">Scheduled for:</Text>
                    <Text>
                      {message.scheduledFor
                        ? format(
                            new Date(message.scheduledFor),
                            "MMM d, yyyy 'at' h:mm a"
                          )
                        : "Not set"}
                    </Text>
                  </HStack>
                  <HStack justify="space-between" fontSize="sm" mt={1}>
                    <Text fontWeight="600">Recipients:</Text>
                    <Text>{recipientCount || 0}</Text>
                  </HStack>
                </Box>
                {!editingMessageId && (
                  <HStack spacing={2} p={2} bg="orange.50" borderRadius="md">
                    <InfoIcon color="orange.500" boxSize={4} />
                    <Text fontSize="sm" color="orange.700">
                      This action cannot be undone. Message will be sent at the
                      scheduled time.
                    </Text>
                  </HStack>
                )}
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={confirmCancelRef} onClick={onConfirmClose}>
                Cancel
              </Button>
              <Button
                colorScheme={editingMessageId ? "blue" : "green"}
                onClick={confirmSchedule}
                ml={3}
                isLoading={isSaving}
              >
                {editingMessageId ? "Update Message" : "Schedule Message"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};
